/**
 * Normalizes GetPickingStatusLogs (picking-service, LIT-394) payloads into a
 * sorted timeline for the UI.
 *
 * Real contract — GET /picking-service/v3/pickingStatusLog/{orderId} (grpc-gateway,
 * default protojson => camelCase names, int64 as strings):
 *   { "orderDetailLogs": [{
 *       "id": "2", "orderId": "10",
 *       "user": { "id": "5", "name": "Picker One" },   // name resolved server-side
 *       "action": "PACKLIST_PACKED",                    // the event/status type
 *       "details": { "packlistId": 123, "packlistTag": "z2all", ... }, // free-form
 *       "createdAt": "2026-07-23T10:00:00Z"
 *   }] }
 *
 * `details` is a free-form Struct: picking-service writes packlistId/packlistTag
 * (and item/order diffs), but not every field on every event, and never a zone.
 * The extra aliases below keep older fixtures/tests parsing too.
 */

// action is the real field; the rest stay as fallbacks for legacy fixtures.
const STATUS_ALIASES = ['action', 'status', 'packlistStatus', 'pickingStatus']
// Prefer the packlist id (what ops identifies a packlist by); fall back to the
// routing tag / reference only when no id is present.
const PACKLIST_ALIASES = [
  'packlistId',
  'packlist_id',
  'packlistReference',
  'packlist_reference',
  'packlistTag',
  'packlist_tag',
  'tag',
]
// The routing tag (z2all / express / others) shown as its own chip.
const TAG_ALIASES = ['packlistTag', 'packlist_tag', 'tag']
const STAFF_ALIASES = [
  'staffName',
  'staff_name',
  'userName',
  'user_name',
  'pickerName',
  'picker_name',
]
const ZONE_ALIASES = ['zone', 'zoneName', 'zone_name']
const TIMESTAMP_ALIASES = [
  'timestamp',
  'createdAt',
  'created_at',
  'eventTime',
  'event_time',
]
const ENVELOPE_ALIASES = [
  'orderDetailLogs',
  'order_detail_logs',
  'entries',
  'pickingStatusLog',
]
// Packlist edits (LIT-415) write a per-item diff into details.itemChanges.
const ITEM_CHANGES_ALIASES = ['itemChanges', 'item_changes']
// picking-service emits this Struct key PascalCased; accept the other casings too.
const CHECK_REQUIRED_ALIASES = [
  'CheckRequired',
  'checkRequired',
  'check_required',
]
// Crate/package edits land here as a diff of the whole packlist.Packages slice.
const PACKAGES_ALIASES = ['Packages', 'packages']

/**
 * Keys the timeline renders itself, plus blobs and bookkeeping that are noise in a
 * log. Everything else in `details` falls through to the generic "other changes"
 * row, so a field added to model.Packlist stays visible without a frontend change.
 */
const DEDICATED_DETAIL_KEYS = [
  ...STATUS_ALIASES,
  ...PACKLIST_ALIASES,
  ...TAG_ALIASES,
  ...ITEM_CHANGES_ALIASES,
  ...CHECK_REQUIRED_ALIASES,
  ...PACKAGES_ALIASES,
]

const IGNORED_DETAIL_KEYS = [
  'ID',
  'id',
  'DeliveryOrderID',
  'CreatedAt',
  'UpdatedAt',
  'Items',
  'DeliveryOrder',
  'OrderAmendments',
  'order_amendments',
]

const pickFirst = (entry, keys) => {
  for (const key of keys) {
    const value = entry?.[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return null
}

// Real packlist/zone data lives inside the free-form `details` Struct; check the
// entry top level first (legacy fixtures) then fall back to details.
const pickFromEntryOrDetails = (entry, keys) =>
  pickFirst(entry, keys) ?? pickFirst(entry?.details, keys)

// picking-service records the picking lifecycle as a `details.status` diff
// ({ newValue, oldValue }, e.g. PENDING -> PICKING -> PACKED). Prefer the
// resulting newValue over `action` (which is often the generic PACKLIST_UPDATED).
const readStatusTransition = (entry) => {
  const statusChange = entry?.details?.status
  if (
    statusChange &&
    typeof statusChange === 'object' &&
    (statusChange.newValue || statusChange.oldValue)
  ) {
    return statusChange.newValue || null
  }
  return null
}

// Quantities arrive as strings ("0", "1.5") from the Struct; compare numerically
// so "1" and 1 are the same value, but keep unparseable input verbatim.
const toQuantity = (value) => {
  if (value === undefined || value === null || value === '') {
    return null
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? String(value) : parsed
}

const readAlias = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return null
}

/**
 * Flattens details.itemChanges into rows the timeline can render. Entries where
 * a field did not actually move (old === new) are flagged as unchanged rather
 * than dropped, so an event that touched an item is still visible.
 */
export const normalizeItemChanges = (details) => {
  const raw = readAlias(details, ITEM_CHANGES_ALIASES)
  if (!Array.isArray(raw)) {
    return []
  }

  return raw
    .filter((change) => change && typeof change === 'object')
    .map((change, index) => {
      const itemId = readAlias(change, ['itemId', 'item_id'])
      const oldQuantity = toQuantity(
        readAlias(change, ['oldQuantity', 'old_quantity'])
      )
      const newQuantity = toQuantity(
        readAlias(change, ['newQuantity', 'new_quantity'])
      )
      const oldStatus = readAlias(change, ['oldStatus', 'old_status'])
      const newStatus = readAlias(change, ['newStatus', 'new_status'])

      return {
        key: `${itemId ?? 'item'}-${index}`,
        itemId: itemId === null ? null : String(itemId),
        itemName: readAlias(change, ['itemName', 'item_name']),
        packlistId: readAlias(change, ['packlistId', 'packlist_id']),
        oldQuantity,
        newQuantity,
        quantityChanged:
          newQuantity !== null && String(oldQuantity) !== String(newQuantity),
        oldStatus,
        newStatus,
        statusChanged: Boolean(newStatus) && oldStatus !== newStatus,
      }
    })
    .filter((change) => change.itemName || change.itemId)
}

/**
 * Reads a { oldValue, newValue } boolean diff out of `details`. Returns null when
 * the flag is absent or did not move, so unchanged flags stay off the timeline.
 */
export const normalizeFlagChange = (details, keys) => {
  const change = readAlias(details, keys)
  if (!change || typeof change !== 'object') {
    return null
  }

  const oldValue = change.oldValue ?? change.old_value
  const newValue = change.newValue ?? change.new_value
  if (newValue === undefined || Boolean(oldValue) === Boolean(newValue)) {
    return null
  }

  return { oldValue: Boolean(oldValue), newValue: Boolean(newValue) }
}

/**
 * picking-service builds `details` by reflecting over the whole model.Packlist
 * (services/deliverylog/create.go getPacklistDetails -> structs.Map), so sql.Null*
 * fields arrive as nested objects rather than scalars:
 *   { "Int64": 55, "Valid": true } / { "Time": "...", "Valid": false }
 * Collapse them to a plain value, or null when the field is not set.
 */
export const unwrapDetailValue = (value) => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value === undefined ? null : value
  }

  if (Array.isArray(value)) {
    return value
  }

  if ('Valid' in value) {
    if (!value.Valid) {
      return null
    }
    const inner = ['Int64', 'String', 'Float64', 'Bool', 'Time'].find(
      (key) => key in value
    )
    return inner ? value[inner] : null
  }

  return value
}

const readPackageCodes = (packages) =>
  (Array.isArray(packages) ? packages : [])
    .map((pkg) =>
      readAlias(pkg, ['PackageCode', 'packageCode', 'package_code'])
    )
    .filter(Boolean)
    .map(String)

/**
 * Reads the details.Packages diff into something renderable.
 *
 * Known gap: crate *removals* do not reach us. picking-service's updatePackages
 * (services/packlist/update.go) appends new packages to packlist.Packages, then
 * markPackagesForDeletion flips DeletedAt on package pointers that the shallow
 * `oldPacklist := *packlist` copy still shares — so both sides of the diff
 * serialize identically and no Packages key is emitted at all. Adding crates grows
 * the slice and does produce a diff. `removed` is kept here because the shape
 * supports it and a service-side deep copy would start populating it.
 */
export const normalizePackagesChange = (details) => {
  const change = readAlias(details, PACKAGES_ALIASES)
  if (!change || typeof change !== 'object' || Array.isArray(change)) {
    return null
  }

  const oldCodes = readPackageCodes(change.oldValue ?? change.old_value)
  const newCodes = readPackageCodes(change.newValue ?? change.new_value)

  const added = newCodes.filter((code) => !oldCodes.includes(code))
  const removed = oldCodes.filter((code) => !newCodes.includes(code))

  if (
    oldCodes.length === newCodes.length &&
    added.length === 0 &&
    removed.length === 0
  ) {
    return null
  }

  return {
    oldCount: oldCodes.length,
    newCount: newCodes.length,
    added,
    removed,
  }
}

// PicklistID -> "Picklist id", owner_name -> "Owner name".
export const humanizeKey = (key) =>
  String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim()
    .replace(/^./, (char) => char.toUpperCase())

/**
 * Catch-all for { oldValue, newValue } pairs the timeline has no dedicated renderer
 * for. Keeps unknown picking-service fields visible instead of silently dropped.
 */
export const normalizeOtherChanges = (details) => {
  if (!details || typeof details !== 'object') {
    return []
  }

  return Object.keys(details)
    .filter(
      (key) =>
        !DEDICATED_DETAIL_KEYS.includes(key) &&
        !IGNORED_DETAIL_KEYS.includes(key)
    )
    .map((key) => {
      const change = details[key]
      if (!change || typeof change !== 'object' || Array.isArray(change)) {
        return null
      }

      const hasOld = 'oldValue' in change || 'old_value' in change
      const hasNew = 'newValue' in change || 'new_value' in change
      if (!hasOld && !hasNew) {
        return null
      }

      const oldValue = unwrapDetailValue(change.oldValue ?? change.old_value)
      const newValue = unwrapDetailValue(change.newValue ?? change.new_value)

      // Nested blob we cannot render meaningfully — better omitted than dumped.
      // (null is fine: it is how an unset sql.Null* comes back.)
      const isBlob = (value) => value !== null && typeof value === 'object'
      if (isBlob(oldValue) || isBlob(newValue)) {
        return null
      }

      return { key, label: humanizeKey(key), oldValue, newValue }
    })
    .filter(Boolean)
}

export const normalizeEntry = (entry, index = 0) => {
  if (!entry || typeof entry !== 'object') {
    return null
  }

  const user = entry.user && typeof entry.user === 'object' ? entry.user : null
  const staffFromUser = user ? user.name : null
  // The account lookup is best-effort server-side (deliverylog/list.go swallows the
  // error and returns an empty map), so keep the id as a fallback identity.
  const staffId = user && user.id ? String(user.id) : null

  const packlistReference = pickFromEntryOrDetails(entry, PACKLIST_ALIASES)
  const packlistTag = pickFromEntryOrDetails(entry, TAG_ALIASES)

  return {
    id:
      entry.id ?? `${pickFirst(entry, TIMESTAMP_ALIASES) || 'entry'}-${index}`,
    status: readStatusTransition(entry) || pickFirst(entry, STATUS_ALIASES),
    packlistReference:
      packlistReference === null || packlistReference === undefined
        ? null
        : String(packlistReference),
    packlistTag:
      packlistTag === null || packlistTag === undefined
        ? null
        : String(packlistTag),
    staffName: pickFirst(entry, STAFF_ALIASES) || staffFromUser || null,
    staffId,
    zone: pickFromEntryOrDetails(entry, ZONE_ALIASES),
    timestamp: pickFirst(entry, TIMESTAMP_ALIASES),
    itemChanges: normalizeItemChanges(entry?.details),
    checkRequired: normalizeFlagChange(entry?.details, CHECK_REQUIRED_ALIASES),
    packagesChange: normalizePackagesChange(entry?.details),
    otherChanges: normalizeOtherChanges(entry?.details),
  }
}

export const extractPickingLogEntries = (response) => {
  const candidates = [
    ...ENVELOPE_ALIASES.map((key) => response?.data?.[key]),
    ...ENVELOPE_ALIASES.map((key) => response?.[key]),
  ]
  const raw = candidates.find(Array.isArray) || []

  return raw
    .map((entry, index) => normalizeEntry(entry, index))
    .filter(Boolean)
    .sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0
      // Newest first to match Delivery Order Log convention
      return bTime - aTime
    })
}

export const formatStatusLabel = (status) => {
  if (!status) {
    return '—'
  }
  return String(status)
    .toLowerCase()
    .split('_')
    .filter((part) => part && part !== 'packlist' && part !== 'status')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const humanize = (value) =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

// picking-service writes PACKLIST_*/ORDER_* actions; map the picking lifecycle to
// pill/node tones, everything else falls through to a neutral, humanized label.
const PICKING_STATUS_MODIFIERS = {
  PICKING: 'picking',
  PACKING: 'packing',
  PACKED: 'packed',
  PICKED: 'packed',
  UPDATED: 'updated',
}

const stripActionPrefix = (action) =>
  String(action || '')
    .toUpperCase()
    .replace(/^PACKLIST_/, '')
    .replace(/^ORDER_/, '')

export const resolvePickingStatus = (action) => {
  const token = stripActionPrefix(action)
  const modifier = PICKING_STATUS_MODIFIERS[token]

  if (modifier) {
    return { modifier, label: humanize(token) }
  }

  return {
    modifier: 'neutral',
    label: humanize(action) || '—',
  }
}
